import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  ArrowUpRight,
  Code,
  Palette,
  Sparkles,
  Video,
  Camera,
  Layout,
  CheckCircle,
  ArrowRight,
  Smartphone
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Services = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);

  useEffect(() => {
    document.title = 'Services | Squarex Studios';

    const ctx = gsap.context(() => {
      gsap.fromTo('.services-hero-title', { opacity: 0, y: 80 }, { opacity: 1, y: 0, duration: 1, ease: 'power4.out' });
      gsap.fromTo('.services-hero-subtitle', { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.8, delay: 0.2, ease: 'power3.out' });

      const blocks = document.querySelectorAll('.service-block');
      blocks.forEach((block) => {
        gsap.fromTo(block, { opacity: 0, y: 60 }, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: block, start: 'top 85%' },
        });
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="services-page">
      <section ref={heroRef} className="relative min-h-[60vh] flex items-center pt-32 pb-20 hero-gradient">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="services-hero-subtitle text-[#af0000] font-mono text-sm uppercase tracking-widest mb-6">Our Services</p>
          <h1 className="services-hero-title text-5xl md:text-6xl lg:text-7xl font-bold font-['Syne'] tracking-tighter leading-[0.95] max-w-4xl">
            Comprehensive solutions for <span className="text-[#af0000]">modern brands</span>
          </h1>
          <p className="services-hero-subtitle text-xl text-[#94a3b8] mt-8 max-w-2xl">
            From Shopify development to AI-powered visuals, we offer end-to-end services that help brands thrive in the digital age.
          </p>
        </div>
      </section>

      <section ref={servicesRef} className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {/* Service 1: Shopify Development */}
            <ServiceBlock
              icon={Code}
              title="Custom Shopify Website Development"
              tagline="E-commerce experiences that convert"
              overview="We build custom Shopify stores that combine stunning design with powerful functionality. From headless commerce to Shopify Plus implementations, we deliver storefronts that drive revenue."
              deliverables={['Custom Shopify theme development', 'Shopify Plus implementation', 'Headless commerce solutions', 'Third-party integrations', 'Performance optimization', 'Migration from other platforms']}
              process={['Discovery & audit', 'UX strategy', 'Custom development', 'QA & testing', 'Launch & support']}
            />

            {/* Service 2: UI/UX Optimization */}
            <ServiceBlock
              icon={Layout}
              title="Shopify UI/UX Optimization"
              tagline="Turn visitors into customers"
              overview="Maximize your conversion rates with data-driven UI/UX improvements. We analyze user behavior, identify friction points, and implement changes that make shopping seamless."
              deliverables={['Conversion rate optimization', 'User experience audits', 'A/B testing implementation', 'Checkout optimization', 'Mobile experience enhancement', 'Heat mapping & analytics']}
              process={['Data analysis', 'UX audit', 'Hypothesis testing', 'Implementation', 'Measurement']}
            />

            {/* Service: iOS & macOS Development */}
            <ServiceBlock
              icon={Smartphone}
              title="iOS & macOS Development"
              tagline="Native apps for the Apple Ecosystem"
              overview="We build high-performance, native applications for iOS and macOS. Leveraging SwiftUI and the latest Apple technologies to deliver premium user experiences."
              deliverables={['Native iOS App Development', 'macOS Desktop Applications', 'iPadOS Optimization', 'WatchOS Apps', 'App Store Submission', 'Performance Engineering']}
              process={['Platform strategy', 'Native UI design', 'Swift development', 'Ecosystem testing', 'Launch']}
            />

            {/* Service 3: Branding */}
            <ServiceBlock
              icon={Sparkles}
              title="Branding & Identity Design"
              tagline="Brands that stand apart"
              overview="Create a distinctive brand identity that resonates with your audience and stands out in the market. From logo design to comprehensive brand systems."
              deliverables={['Logo & wordmark design', 'Brand guidelines', 'Color & typography systems', 'Brand collateral', 'Social media assets', 'Packaging design']}
              process={['Brand discovery', 'Concept development', 'Design exploration', 'Refinement', 'Guidelines creation']}
            />

            {/* Service 4: Design Systems */}
            <ServiceBlock
              icon={Palette}
              title="UI/UX Design Systems"
              tagline="Consistent, scalable interfaces"
              overview="Build cohesive design systems that ensure consistency across all touchpoints. Component libraries, documentation, and design tokens for scalable product design."
              deliverables={['Component libraries', 'Design tokens', 'Documentation', 'Figma/Sketch libraries', 'Accessibility guidelines', 'Animation standards']}
              process={['Audit existing design', 'Define foundations', 'Build components', 'Document patterns', 'Training & handoff']}
            />

            {/* Service 5: Video */}
            <ServiceBlock
              icon={Video}
              title="Video Editing & Motion Graphics"
              tagline="Stories that move people"
              overview="Compelling video content that captures attention and communicates your message. From social media cuts to full campaign videos and motion graphics."
              deliverables={['Brand films', 'Social media video content', 'Motion graphics', 'Product videos', 'Animated explainers', 'Video advertising']}
              process={['Creative brief', 'Storyboarding', 'Production', 'Editing & effects', 'Delivery & formats']}
            />

            {/* Service 6: AI Photo Shoots - Featured */}
            <ServiceBlock
              icon={Camera}
              title="AI Photo Shoot Production"
              tagline="SQRX ShotLab"
              overview="Revolutionary AI-powered product photography that delivers studio-quality results without traditional photo shoots. Faster, more affordable, and infinitely scalable."
              deliverables={['Studio product shots', 'Lifestyle photography', 'Campaign visuals', 'Environment builds', 'Model compositions', 'Batch processing']}
              process={['Brief & reference', 'AI generation', 'Refinement', 'Post-processing', 'Asset delivery']}
              featured={true}
            />
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight mb-6">
            Not sure which service <span className="text-[#af0000]">you need?</span>
          </h2>
          <p className="text-xl text-[#94a3b8] mb-10">Let's talk about your goals and figure out the best approach together.</p>
          <Link to="/contact" data-testid="services-cta-btn" className="btn-primary inline-flex items-center gap-2">
            Book a Consultation <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

const ServiceBlock = ({ icon: Icon, title, tagline, overview, deliverables, process, featured }) => (
  <div data-testid="service-block" className={`service-block grid lg:grid-cols-2 gap-12 items-start ${featured ? 'p-8 md:p-12 bg-gradient-to-br from-[#af0000]/10 to-transparent border border-[#af0000]/20' : ''}`}>
    <div>
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-12 h-12 flex items-center justify-center ${featured ? 'bg-[#af0000]' : 'bg-white/10'}`}>
          <Icon size={24} className={featured ? 'text-white' : 'text-[#af0000]'} />
        </div>
        {featured && <span className="px-3 py-1 bg-[#af0000] text-white text-xs font-semibold rounded-full">Featured</span>}
      </div>
      <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] tracking-tight mb-3">{title}</h2>
      <p className="text-[#af0000] font-medium mb-6">{tagline}</p>
      <p className="text-[#94a3b8] leading-relaxed mb-8">{overview}</p>
      {featured ? (
        <Link to="/shotlab" className="btn-primary inline-flex items-center gap-2">Explore ShotLab <ArrowUpRight size={18} /></Link>
      ) : (
        <Link to="/contact" className="btn-secondary inline-flex items-center gap-2">Get Started <ArrowRight size={18} /></Link>
      )}
    </div>
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-[#94a3b8] mb-4">Deliverables</h3>
        <div className="grid grid-cols-2 gap-3">
          {deliverables.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <CheckCircle size={16} className="text-[#af0000] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#f8fafc]">{item}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-[#94a3b8] mb-4">Process</h3>
        <div className="flex flex-wrap gap-3">
          {process.map((step, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center bg-[#af0000]/20 text-[#af0000] text-xs font-bold">{idx + 1}</span>
              <span className="text-sm text-[#f8fafc]">{step}</span>
              {idx < process.length - 1 && <ArrowRight size={14} className="text-[#64748b]" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default Services;
